import { Component, Emit, Prop, Ref, Vue } from 'vue-property-decorator'
import LiquorTree, { LiquorTreeNodeStructure, LiquorTreeOptions } from 'liquor-tree'
import { billingAppliedBillableConceptService } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.service'
import {
    AppliedBillableConceptEntityType,
    HierarchicalBillingCaseTreeResponse,
    HierarchicalBillingPortionTreeResponse,
    HierarchicalBillingSampleTreeResponse,
    HierarchicalBillingSubportionTreeResponse
} from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { StrictNullable } from '@/shared/types/app.types'
import {
    initialValuableConceptsModalTreeState,
    ValuableConceptsModalTreeState,
    ValuableConceptsTreeEntities,
    ValuableConceptsTreeNode,
    ValuableConceptsTreeNodeStructure
} from '@/components/valuable-concepts-modal/valuable-concepts-modal-tree/valuable-concepts-modal-tree.model'
import ValuableConceptsModalSkeletonsTree from '@/components/valuable-concepts-modal/shared/components/skeletons/valuable-concepts-modal-skeletons-tree/valuable-concepts-modal-skeletons-tree.vue'
import { ValuableConceptsShowingPropertiesEntitySelected } from '@/components/valuable-concepts-modal/valuable-concepts-modal.model'

@Component({
    components: {
        LiquorTree,
        ValuableConceptsModalSkeletonsTree
    }
})
export default class ValuableConceptsModalTree extends Vue {
    @Ref() readonly hierarchicalTree!: LiquorTree
    @Prop() readonly caseNumber!: string
    @Prop() readonly caseId!: number
    @Prop() readonly accountingUnitId!: number
    @Prop() readonly selectedNodeTree!: StrictNullable<ValuableConceptsTreeNodeStructure>
    @Prop() readonly initialSelectedNodeTree!: StrictNullable<ValuableConceptsShowingPropertiesEntitySelected>

    @Emit()
    selectedNode(node: StrictNullable<ValuableConceptsTreeNodeStructure>): StrictNullable<ValuableConceptsTreeNodeStructure> {
        return node
    }

    state: ValuableConceptsModalTreeState
    constructor() {
        super()
        this.state = initialValuableConceptsModalTreeState()
    }

    get treeOptions(): LiquorTreeOptions {
        return this.state.treeOptions
    }

    get hasData(): boolean {
        return !!this.state.data
    }

    mounted(): void {
        this.loadTree()
    }

    loadTree(): void {
        this.state.promise = this._getHierarchicalTree()
    }

    onNodeSelected(selected: LiquorTreeNodeStructure<ValuableConceptsTreeNode>): void {
        this.selectedNode(selected)
    }

    onTreeMounted(): void {
        this.hierarchicalTree.setModel(this.state.data)
        this._expandTree()
    }

    private async _getHierarchicalTree(): Promise<void> {
        const laboratoryId = this.$store.getters.labId
        const response: StrictNullable<HierarchicalBillingCaseTreeResponse> = await this.$_toCallSecure(
            billingAppliedBillableConceptService.getHierarchicalBillingCaseTree(
                laboratoryId,
                this.caseId,
                this.accountingUnitId
            )
        )
        this.state.data = response ? this._parseEntityCase(response) : null
    }

    private _mapNodes(
        node: ValuableConceptsTreeEntities,
        entityType: AppliedBillableConceptEntityType,
        caseId: StrictNullable<number>,
        sampleId: StrictNullable<number>,
        portionId: StrictNullable<number>
    ): ValuableConceptsTreeNodeStructure {
        switch (entityType) {
        case AppliedBillableConceptEntityType.SAMPLE:
            return this._parseEntitySample(node as HierarchicalBillingSampleTreeResponse, caseId)
        case AppliedBillableConceptEntityType.PORTION:
            return this._parseEntityPortion(node as HierarchicalBillingPortionTreeResponse, caseId, sampleId)
        case AppliedBillableConceptEntityType.SUBPORTION:
            return this._parseEntitySubportion(node as HierarchicalBillingSubportionTreeResponse, caseId, sampleId, portionId)
        default:
            return this._parseEntityCase(node as HierarchicalBillingCaseTreeResponse)
        }
    }

    private _expandTree(): void {
        this.hierarchicalTree.expandAll()
        if (this.selectedNodeTree) {
            this.selectedNode(this.selectedNodeTree)
            this._scrollIntoNode(this.selectedNodeTree?.data.fullLabel!)
        } else {
            const preselection = this.hierarchicalTree.selected()
            this.selectedNode(preselection?.[0])
        }
    }

    private _scrollIntoNode(id: string): void {
        setTimeout(() => {
            const node = document.getElementById(id)
            if (node) {
                node.scrollIntoView({ block: 'end', behavior: 'smooth' })
            }
        }, 100)
    }

    private _parseEntityCase(hierarchicalTree: HierarchicalBillingCaseTreeResponse): ValuableConceptsTreeNodeStructure {
        const isSelected = this.initialSelectedNodeTree
            ? (this.initialSelectedNodeTree?.entityId === hierarchicalTree.caseId && this.initialSelectedNodeTree.entityType === AppliedBillableConceptEntityType.CASE)
            : (!this.selectedNodeTree?.data || (this.selectedNodeTree?.data?.caseId === hierarchicalTree.caseId && this.selectedNodeTree?.data?.entityType === AppliedBillableConceptEntityType.CASE))
        const parsedCase: ValuableConceptsTreeNodeStructure = {
            text: hierarchicalTree.caseNumber,
            state: { selected: isSelected },
            data: {
                id: hierarchicalTree.caseId,
                entityType: AppliedBillableConceptEntityType.CASE,
                label: hierarchicalTree.caseNumber,
                fullLabel: hierarchicalTree.caseNumber,
                icon: 'fal fa-chart-network',
                value: hierarchicalTree.billableValue,
                entityTypeLabel: hierarchicalTree.caseType.description,
                caseId: hierarchicalTree.caseId
            },
            children: []
        }
        hierarchicalTree?.samples.forEach((sampleTreeItem: HierarchicalBillingSampleTreeResponse) => {
            const sampleTreeItemNode = this._mapNodes(sampleTreeItem, AppliedBillableConceptEntityType.SAMPLE, hierarchicalTree.caseId, null, null)
            parsedCase.children!.push(sampleTreeItemNode)
        })
        return parsedCase
    }

    private _parseEntitySample(
        node: HierarchicalBillingSampleTreeResponse,
        caseId: StrictNullable<number>
    ): ValuableConceptsTreeNodeStructure {
        const parsedSample: ValuableConceptsTreeNodeStructure = {
            text: node.sampleNumber,
            state: { selected: (this.initialSelectedNodeTree?.entityId === node.sampleId && this.initialSelectedNodeTree.entityType === AppliedBillableConceptEntityType.SAMPLE) ||
                (this.selectedNodeTree?.data?.sampleId === node.sampleId && this.selectedNodeTree?.data?.entityType === AppliedBillableConceptEntityType.SAMPLE)
            },
            data: {
                id: node.sampleId,
                entityType: AppliedBillableConceptEntityType.SAMPLE,
                label: node.sampleNumber,
                fullLabel: node.sampleLabel,
                icon: 'fal fa-prescription-bottle',
                value: node.billableValue,
                entityTypeLabel: node.sampleType.description,
                caseId: caseId!,
                sampleId: node.sampleId
            },
            children: []
        }
        node.portions.forEach((portion: HierarchicalBillingPortionTreeResponse) => {
            parsedSample.children!.push(this._mapNodes(portion, AppliedBillableConceptEntityType.PORTION, caseId, node.sampleId, null))
        })
        return parsedSample
    }

    private _parseEntityPortion(
        node: HierarchicalBillingPortionTreeResponse,
        caseId: StrictNullable<number>,
        sampleId: StrictNullable<number>
    ): ValuableConceptsTreeNodeStructure {
        const parsedPortion:ValuableConceptsTreeNodeStructure = {
            text: node.portionNumber,
            state: { selected: (this.initialSelectedNodeTree?.entityId === node.portionId && this.initialSelectedNodeTree.entityType === AppliedBillableConceptEntityType.PORTION) ||
                (this.selectedNodeTree?.data?.portionId === node.portionId && this.selectedNodeTree?.data?.entityType === AppliedBillableConceptEntityType.PORTION)
            },
            data: {
                id: node.portionId,
                entityType: AppliedBillableConceptEntityType.PORTION,
                label: node.portionNumber,
                fullLabel: node.portionLabel,
                icon: 'icon-block',
                value: node.billableValue,
                entityTypeLabel: node.portionType.potName,
                caseId: caseId!,
                sampleId: sampleId!,
                portionId: node.portionId
            },
            children: []
        }
        node.subportions.forEach((subportion: HierarchicalBillingSubportionTreeResponse) => {
            parsedPortion.children!.push(this._mapNodes(subportion, AppliedBillableConceptEntityType.SUBPORTION, caseId, sampleId, node.portionId))
        })
        return parsedPortion
    }

    private _parseEntitySubportion(
        node: HierarchicalBillingSubportionTreeResponse,
        caseId: StrictNullable<number>,
        sampleId: StrictNullable<number>,
        portionId: StrictNullable<number>
    ): ValuableConceptsTreeNodeStructure {
        return {
            text: node.subportionNumber,
            state: {
                selected: (this.initialSelectedNodeTree?.entityId === node.subportionId && this.initialSelectedNodeTree.entityType === AppliedBillableConceptEntityType.SUBPORTION) ||
                (this.selectedNodeTree?.data?.subportionId === node.subportionId && this.selectedNodeTree?.data?.entityType === AppliedBillableConceptEntityType.SUBPORTION)
            },
            data: {
                id: node.subportionId,
                entityType: AppliedBillableConceptEntityType.SUBPORTION,
                label: node.subportionNumber,
                fullLabel: node.subportionLabel,
                icon: 'icon-porta',
                value: node.billableValue,
                entityTypeLabel: node.subportionType.sptName,
                caseId: caseId!,
                sampleId: sampleId!,
                portionId: portionId!,
                subportionId: node.subportionId
            },
            children: []
        }
    }
}
